import { isIosCompanionApp } from "@/lib/native/runtime";
import { Link } from "react-router-dom";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { keys } from "@/lib/i18n/portal";
import { useStoreSubscriptionQuery } from "@/shared/subscriptions/services/useStoreSubscriptionQuery";

type Props = {
  storeSlug: string;
  /**
   * Extra spacing for pages that provide no top padding of their own. The
   * orders page deliberately has none — its sticky header owns that space so it
   * doesn't visibly travel before pinning — which leaves the banner butting
   * straight against the chrome. Every other page brings its own padding and
   * wants the banner flush.
   */
  className?: string;
};

/**
 * Global banner for a per-order store whose wallet cannot cover the next order.
 *
 * Deliberately a SEPARATE component from `SubscriptionRestrictedBanner`: that
 * one keys off `isRestricted`, which is false for per-order stores by design,
 * and the two states are not the same news. A restricted store is stopped —
 * every write is blocked. A per-order store with an empty wallet is still
 * trading normally; only the customer details on uncovered orders are held.
 * Bending the restricted banner to cover both would have to lie about one.
 *
 * Tone follows suit: `role="status"` and the warning palette, not `role="alert"`
 * and the error palette. This is the seller's own unpaid bill, not a failure.
 * The action is a link to the wallet screen, where the seller files a top-up
 * request for an admin to confirm — a2z still has no self-serve payment, but
 * "contact the admin team" was a dead end where a link belonged.
 */
export function StoreWalletShortBanner({ storeSlug, className }: Props) {
  const t = useTranslations();
  const locale = useLocale();
  const { data } = useStoreSubscriptionQuery({ storeSlug });

  // Restriction is the harder stop and gets its own banner; never stack both.
  if (!data?.wallet?.isShort || data.isRestricted) {
    return null;
  }

  return (
    <div
      role="status"
      className={[
        "border-b border-[color:var(--warning-500)] bg-[color:var(--warning-100)] px-4 py-3 text-[color:var(--warning-700)]",
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <p className="text-[13px] font-semibold">
        {t(keys.seller.walletShortBannerTitle)}
      </p>
      <p className="mt-1 text-[13px] leading-[1.55]">
        {t(isIosCompanionApp() ? keys.iosCompanion.restricted : keys.seller.walletShortBannerMessage)}
      </p>
      {!isIosCompanionApp() ? <Link
        to={getSellerPath(locale, storeSlug, "/payments")}
        className="mt-1 inline-flex text-[13px] font-bold underline underline-offset-4"
      >
        {t(keys.seller.walletShortBannerAction)}
      </Link> : null}
    </div>
  );
}
